import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useBreakpointValue, LinkBox} from '@chakra-ui/react'
import Link from '@app/components/link'
import {Component} from '@salesforce/commerce-sdk-react/components'
import PDCTitle from '../../../components/pdc-title'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import {useUrlNotLocalizedFromLink} from '../../helpers/urlHelper'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {TurtleIcon} from '../../../components/custom-icons/index.jsx'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * PDCHomePatternFinderPush is a React functional component designed for the Page Designer Canvas (PDC) home layout.
 * It renders a visually rich, responsive section that combines a title, subtitle, description, optional secondary image,
 * and a main image with flexible placement (left/top or right/bottom). The component supports dynamic region-based content,
 * customizable styles (background color, text color, title font color), and responsive images for various breakpoints.
 * It also provides accessibility features, skeleton loading states for images, and overlay link functionality for both
 * text and image blocks. The component is highly configurable via its props, allowing for integration of custom links,
 * images, and nested components, and is intended for use within a modular, region-based page layout system.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The main title text.
 * @param {string|number} [props.titleHeadingLevel] - The heading level for the title (e.g., h1, h2).
 * @param {string} [props.titleFontColor] - The color of the title text.
 * @param {string} [props.subTitle] - The subtitle or descriptor text (HTML allowed).
 * @param {string} [props.desc] - The description text (HTML allowed).
 * @param {boolean} [props.secondaryImage] - Whether to display a secondary image (e.g., TurtleIcon).
 * @param {string} [props.backgroundColor] - The background color for the text block.
 * @param {string} [props.textColor] - The text color for the content.
 * @param {string} [props.smallImage] - URL for the small breakpoint image.
 * @param {string} [props.mediumImage] - URL for the medium breakpoint image.
 * @param {string} [props.largeImage] - URL for the large breakpoint image.
 * @param {string} [props.xlargeImage] - URL for the extra-large breakpoint image.
 * @param {string} [props.xxlargeImage] - URL for the double extra-large breakpoint image.
 * @param {string} [props.imageTitle] - The title attribute for the main image.
 * @param {string} [props.imageAlt] - The alt attribute for the main image.
 * @param {string} [props.imgPlacement] - Placement of the image ('left (top)' or other).
 * @param {string} [props.wrapperTextUrl] - URL for the text block overlay link.
 * @param {string} [props.wrapperImageUrl] - URL for the image block overlay link.
 * @param {Array} [props.regions] - Array of region objects containing nested components.
 * @param {Object} [props.custom] - Custom configuration object (e.g., custom images, URLs).
 * @param {boolean} [props.displayPriority] - If true, prioritizes image loading for above-the-fold content.
 *
 * @returns {JSX.Element} A responsive, accessible section combining text and image blocks with optional nested components and overlay links.
 */
export const PDCHomePatternFinderPush = ({
    title,
    titleHeadingLevel,
    titleFontColor,
    subTitle,
    desc,
    secondaryImage,
    backgroundColor,
    textColor,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    imgPlacement,
    wrapperTextUrl,
    wrapperImageUrl,
    regions,
    custom,
    displayPriority
}) => {
    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    const titleId = useId()
    const textUrl = useUrlNotLocalizedFromLink(custom?.wrapperTextUrl) || wrapperTextUrl
    const imageUrl = useUrlNotLocalizedFromLink(custom?.wrapperImageUrl) || wrapperImageUrl
    const componentUrl = textUrl === imageUrl ? textUrl : null

    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    const txtBlock = (
        <Flex
            width={['100%', null, '50%']}
            minHeight={['133vw', null, 'auto']}
            direction="column"
            backgroundColor={backgroundColor}
        >
            <WrapperLink
                url={textUrl != imageUrl ? textUrl : null}
                isOverlay={components.length > 0}
                flex={1}
                display="flex"
                flexDirection="column"
                color={textColor}
            >
                <Flex
                    flexDirection="column"
                    justifyContent="space-between"
                    alignItems={'center'}
                    py={'45px'}
                    px={'20px'}
                    height={'100%'}
                    minHeight={'inherit'}
                    flex={1}
                >
                    <Flex
                        flexDirection="column"
                        justifyContent="center"
                        alignItems={'center'}
                        w={'100%'}
                    >
                        <Text
                            dangerouslySetInnerHTML={{
                                __html: subTitle
                            }}
                            variant="descriptor"
                            color={textColor ? textColor : 'fullBlack'}
                        />
                        <PDCTitle
                            width={['90%', null, '100%']}
                            title={title}
                            titleFontColor={titleFontColor}
                            titleHeadingLevel={titleHeadingLevel}
                            txtHorizontalPos={'center'}
                            textColor={textColor}
                            id={titleId}
                            size={['5xl', null, null, '6xl']}
                            mt={[24, null, 34]}
                        />
                        {desc && (
                            <Text
                                dangerouslySetInnerHTML={{
                                    __html: desc
                                }}
                                variant="descriptor"
                                textAlign={'center'}
                                mt={[24, null, 34]}
                            />
                        )}
                        {secondaryImage && (
                            <TurtleIcon
                                width={{base: '193px', lg: '315px'}}
                                height="auto"
                                mt={[40, null, null, 114]}
                                color={textColor}
                            />
                        )}
                    </Flex>

                    {components &&
                        components.length > 0 &&
                        components.map((component, index) => {
                            component.backgroundColor = backgroundColor
                            component.textColor = textColor
                            component.txtHorizontalPos = 'center'
                            component.width = 'auto'
                            component.custom.isOverlay = !!textUrl
                            component.custom.url = custom?.wrapperTextUrl
                            return (
                                <Box mt={[16, null, null, 0]} key={component?.id || index}>
                                    <Component component={component} />
                                </Box>
                            )
                        })}
                </Flex>
            </WrapperLink>
        </Flex>
    )

    const imgBlock = (
        <Box width={['100%', '100%', '50%']}>
            <PDCImage
                currentImage={currentImage}
                imageWidths={DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                url={textUrl != imageUrl ? imageUrl : null}
                displayPriority={displayPriority}
                title={title}
                skeletonProps={{aspectRatio: ['207 / 275', null, '143 / 180'], width: '100%'}}
            />
        </Box>
    )

    return (
        <Box as="section" aria-describedby={titleId} width="100%" height={'auto'}>
            <WrapperLink url={componentUrl} isOverlay={components.length > 0}>
                <Flex
                    flexDirection={['column', 'column', 'row']}
                    justifyContent="space-between"
                    alignItems="stretch"
                    width="100%"
                >
                    {imgPlacement === 'left (top)' ? (
                        <>
                            {imgBlock}
                            {txtBlock}
                        </>
                    ) : (
                        <>
                            {txtBlock}
                            {imgBlock}
                        </>
                    )}
                </Flex>
            </WrapperLink>
        </Box>
    )
}

PDCHomePatternFinderPush.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    subTitle: PropTypes.string,
    desc: PropTypes.string,
    secondaryImage: PropTypes.bool,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    txtHorizontalPos: PropTypes.string,
    imgPlacement: PropTypes.string,
    wrapperImageUrl: PropTypes.string,
    wrapperTextUrl: PropTypes.string,
    custom: PropTypes.object,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    ),
    displayPriority: PropTypes.bool
}

/**
 * WrapperLink is a flexible link component that conditionally renders either a Link or LinkBox
 * based on the `isOverlay` prop. It wraps its children with the appropriate link component,
 * forwarding additional props and ensuring no text decoration by default.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The content to be wrapped by the link.
 * @param {string} props.url - The URL to navigate to when the link is clicked.
 * @param {boolean} props.isOverlay - Determines whether to use LinkBox (if true) or Link (if false).
 * @param {...Object} [props] - Additional props to be passed to the rendered link component.
 * @returns {React.ReactNode} The children wrapped in a link component if `url` is provided, otherwise just the children.
 */
export const WrapperLink = ({children, url, isOverlay, ...props}) => {
    const Wrapper = isOverlay ? LinkBox : Link
    return url ? (
        <Wrapper
            to={isOverlay ? undefined : url}
            href={isOverlay ? undefined : url}
            textDecoration="none"
            {...props}
        >
            {children}
        </Wrapper>
    ) : (
        children
    )
}

WrapperLink.propTypes = {
    children: PropTypes.node,
    url: PropTypes.string,
    isExternal: PropTypes.bool,
    isOverlay: PropTypes.bool
}
